<template>
    <Teleport to="body">
        <div
            class="hts-modal"
            :style="{ 'z-index': 2000 + (uid || 0) }"
            @focus="handleFocus"
            @click="handleClick">
            <input
                :id="'model-' + uid"
                v-model="visible"
                type="checkbox"
                class="modal-toggle" >
            <label
                :for="'model-' + uid"
                class="modal cursor-pointer modal-middle"
                @click="handleBackdropClick">
                <span
                    class="modal-box relative"
                    :style="cStyle"
                    :class="className">
                    <div class="flex justify-end items-top">
                        <h3
                            v-if="title"
                            class="w-full">
                            {{ title }}
                        </h3>

                        <label
                            v-if="!hideClose && !autoClose"
                            :for="'model-' + uid"
                            class="btn btn-sm btn-circle z-20 -translate-y-1"
                            @click="handleCloseClick">
                            ✕
                        </label>
                    </div>

                    <slot :hide="hide">
                        <template v-if="content">
                            <span>
                                {{ content }}
                            </span>
                        </template>
                    </slot>
                    <slot name="custom" />
                    <template v-if="type === 'confirm' || mType === 'confirm'">
                        <div class="modal-action">
                            <hts-form-button
                                :disabled="disabled"
                                :loading="loading"
                                color="primary"
                                icon-position="right"
                                type="submit"
                                :outline="btnOutline"
                                @click="handleConfirm">
                                {{ $t(confirmLabel) }}
                            </hts-form-button>

                            <span
                                :for="'model-' + uid"
                                class="btn"
                                :class="{ 'btn-square': btnType === 'square', 'btn-circle': btnType === 'circle', 'btn-outline': btnOutline }"
                                @click="handleCloseClick">
                                {{ $t(cancelLabel) }}
                            </span>
                        </div>
                    </template>
                </span>
            </label>
        </div>
    </Teleport>
</template>
<script setup lang="ts">
import { getCurrentInstance } from 'vue'
import screen from '~/components/utils/screen'

const uid = getCurrentInstance()?.uid
const modelBox = templateRef<HTMLElement>('modelBox')
const breakpoint = screen()
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: undefined
  },
  title: String,
  content: String,
  size: [String, Number],
  show: Boolean,
  type: String,
  mType: String,
  hideClose: Boolean,
  disabled: Boolean,
  loading: Boolean,
  cancelLabel: {
    type: String,
    default: 'labels.cancel'
  },
  confirmLabel: {
    type: String,
    default: 'labels.confirm'
  },
  btnType: String,
  btnOutline: Boolean,
  autoClose: Boolean,
  className: String,
  clickOutsideClose: {
    type: Boolean,
    default: true
  }
})
const { modelValue, size, show, loading, autoClose, clickOutsideClose } = toRefs(props)

const emits = defineEmits(['update:modelValue', 'show', 'hide', 'close', 'confirm', 'focus', 'body-click', 'click'])

const whiteAttr = ['radio-box', 'check-box']

const tempVisible = ref<boolean>(false)

const visible = computed({
  get (): boolean {
    return modelValue === undefined || modelValue.value === undefined ? tempVisible.value : modelValue.value
  },
  set (val: boolean) {
    tempVisible.value = val
    emits('update:modelValue', val)
    if (!val) {
      emits('close')
    }
  }
})

watch(
  () => show.value,
  (val: boolean) => {
    visible.value = val
  }
)
watch(
  () => visible.value,
  (val: boolean) => {
    if (val) {
      emits('show')
    }
  }
)
watch(
  () => modelValue?.value,
  (val: boolean) => {
    visible.value = !!val
  }
)

const cStyle = computed(() => {
  if (breakpoint.value.isMobile) {
    return {
      width: '100%'
    }
  }
  if (size && size.value) {
    if (typeof size.value === 'number') {
      return {
        width: size.value + '%',
        maxHeight: size.value + '%'
      }
    }
    if (typeof size.value === 'string') {
      switch (size.value) {
      case 'small':
        return {
          width: '20%',
          maxHeight: '20%'
        }
      case 'medium':
        return {
          width: '50%',
          maxHeight: '50%'
        }
      case 'large':
        return {
          width: '70%',
          maxHeight: '70%'
        }
      case 'full':
        return {
          width: '100%',
          maxHeight: '100%'
        }
      default:
        return {
          width: size.value,
          maxHeight: size.value
        }
      }
    }
  }
  return {
    width: '40%'
  }
})

const hide = () => {
  visible.value = false
  emits('hide')
}

const handleConfirm = (e: any) => {
  if (e instanceof MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
  }
  if (!loading.value) {
    emits('confirm')
  }
}
const handleFocus = (e: any) => {
  if (e instanceof MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
  }
  emits('focus', e)
}
const handleCloseClick = (e: any) => {
  if (e instanceof MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
  }
  hide()
}
const handleBackdropClick = (e: any) => {
  const attr = e.target.getAttribute('data-type')
  if (autoClose.value) {
    return
  }

  if (!clickOutsideClose.value) {
    return
  }
  if (!e.target.closest('.modal-box')) {
    hide()
  }
}
const handleClick = (e: any) => {
  const attr = e.target.getAttribute('data-type')
  if (!e.target.closest('.modal-box')) {
    e.preventDefault()
  }
  emits('body-click', e)
}
</script>
